import { AvField, AvForm } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { Col, Input, Label, Row, Spinner } from "reactstrap";
import Select from "react-select";
import { Accordion } from "react-bootstrap";
import { useEffect, useState } from "react";
import ResponsiblePersonImage from "src/modules/data-protection/assets/images/responsible-person.svg";
import { useMutation, useQuery } from "@tanstack/react-query";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { RiCheckLine, RiCloseCircleLine } from "react-icons/ri";

const useAutoResetMutation = (mutationFn, resetTime = 3000) => {
    const mutation = useMutation(mutationFn);

    useEffect(() => {
        let timer;
        if (mutation.isSuccess || mutation.isError) {
            timer = setTimeout(() => {
                mutation.reset();
            }, resetTime);
        }
        return () => clearTimeout(timer);
    }, [mutation.isSuccess, resetTime]);

    return mutation;
};

const ResponsiblePerson = ({
  t,
  assetId,
  handleFetchSoftwareDetailsQuery,
  users,
  selectedCompany,
  selectedPosition,
  selectedPhoneNumber,
  selectedEmail,
  selectedDepartment,
  selectedResponsiblePerson,
}) => {
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [assetTypes, setAssetTypes] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);

  // Modules
  // Fetch
  const handelFetchModulesListQuery = useQuery({
    queryKey: ["data-protection-modules-list", moduleOptions],
    queryFn: async () => {
      const service = DataProtectionSoftwareService.getInstance();

      const response = await service.modules();
      const data =
        response && response.length
          ? response.map((r) => ({
              label: r.name,
              value: r.id,
            }))
          : [];
      setModuleOptions(data);
      return data;
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      if (process.env.NODE_ENV === "development") console.error(error);

      toast(t("An error occurred while fetching asset types."), {
        type: "error",
      });
    },
  });

  // Department
  const handleCreateDepartmentMutation = useAutoResetMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changeDepartment(payload);
    }
  });

  const debouncedHandleDepartmentChange = debounce((event) => {
    const newDepartment = event.target.value;
    handleCreateDepartmentMutation.mutate({
      software: Number(assetId),
      department: newDepartment,
    });
  }, 900);

  // Phone Number
  const handleCreatePhoneNumberMutation = useAutoResetMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changePhoneNumber(payload);
    }
  });

  const debouncedHandlePhoneNumberChange = debounce((event) => {
    const newData = event.target.value;
    handleCreatePhoneNumberMutation.mutate({
      software: Number(assetId),
      phoneNumber: newData,
    });
  }, 900);

  // Email
  const handleCreateEmailMutation = useAutoResetMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changeEmail(payload);
    }
  });

  const debouncedHandleEmailChange = debounce((value) => {
    handleCreateEmailMutation.mutate({
      software: Number(assetId),
      email: value
    });
  }, 900);

  // Responsible Person
  const handleCreateResponsiblePersonMutation = useMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changeResponsiblePerson(payload);
    },
    onSuccess: () => {
      handleFetchSoftwareDetailsQuery.refetch();
      toast(t("Responsible person changed successfully."), {
        type: "success",
      });
    },
    onError: () => {
      toast(t("An error occurred while changing responsible person."), {
        type: "error",
      });
    },
  });

  const debouncedHandleResponsiblePersonChange = debounce((event) => {
    handleCreateResponsiblePersonMutation.mutate({
      software: Number(assetId),
      user: String(event.value),
    });
  }, 900);

  // Company
  const handleCompanyMutation = useAutoResetMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changeCompany(payload);
    }
  });

  const debouncedHandleCompanyChange = debounce((event) => {
    const newData = event.target.value;
    handleCompanyMutation.mutate({
      software: Number(assetId),
      company: newData,
    });
  }, 900);

  // Position
  const handlePositionMutation = useAutoResetMutation({
    mutationFn: async (payload) => {
      const service = DataProtectionSoftwareService.getInstance();
      return await service.changePosition(payload);
    }
  });

  const debouncedHandlePositionChange = debounce((event) => {
    const newData = event.target.value;
    handlePositionMutation.mutate({
      software: Number(assetId),
      position: newData,
    });
  }, 900);

  useEffect(() => {
    if (handelFetchModulesListQuery.data) {
      const gotModules = handelFetchModulesListQuery.data;
      if (gotModules && Array.isArray(gotModules)) {
        setAssetTypes(
          gotModules.map((gotModule) => {
            return {
              label: gotModule,
              value: gotModule,
            };
          })
        );
      }
    }
  }, [handelFetchModulesListQuery.data]);

  return (
    <Accordion className="mt-4" defaultActiveKey={activeAccordion}>
      <Accordion.Item
        onClick={() => setActiveAccordion("asset-details")}
        className={activeAccordion === "asset-details" ? "active" : ""}
        eventKey={"asset-details"}
      >
        <Accordion.Header>
          <img src={ResponsiblePersonImage} alt="" />
          <span>{t("Responsible Person")}</span>
        </Accordion.Header>

        <Accordion.Body>
          <Row>
            <Col sm="12" md="3" className="mb-4">
              <Label className="form-label">{t("Responsible Person")}:</Label>
              <Select
                className="mt-1 text-capitalize"
                placeholder={t("Select") + "..."}
                menuPortalTarget={document.body}
                id="assignedAnalyst"
                name="assignedAnalyst"
                isError={handleFetchSoftwareDetailsQuery?.isError}
                value={selectedResponsiblePerson}
                options={users}
                onChange={debouncedHandleResponsiblePersonChange}
              />
            </Col>

            <Col sm="12" md="3" className="mb-4">
              <Label className="form-label">{t("Department")}:</Label>
              <Input
                name="department"
                type="text"
                className="form-control"
                onChange={debouncedHandleDepartmentChange}
                id="department"
                defaultValue={selectedDepartment}
              />

            {handleCreateDepartmentMutation.isLoading && (
                <>
                    <Spinner size='sm' color="info" />
                    <small className="text-info ms-1">
                        {t("Saving")}...
                    </small>
                </>
            )}

            {handleCreateDepartmentMutation.isError && (
                <small className="text-danger">
                    <RiCloseCircleLine size="16" className="me-1" />
                    {t("Unable to save changes")}
                </small>
            )}

            {handleCreateDepartmentMutation.isSuccess && (
                <small className="text-success">
                    <RiCheckLine size="16" className="me-1" />
                    {t("Changes saved")}
                </small>
            )}
            </Col>

            <Col sm="12" md="3" className="mb-4">
              <Label className="form-label">{t("Company")}:</Label>
              <Input
                name="company"
                type="text"
                className="form-control"
                onChange={debouncedHandleCompanyChange}
                id="company"
                defaultValue={selectedCompany}
              />

            {handleCompanyMutation.isLoading && (
                <>
                    <Spinner size='sm' color="info" />
                    <small className="text-info ms-1">
                        {t("Saving")}...
                    </small>
                </>
            )}

            {handleCompanyMutation.isError && (
                <small className="text-danger">
                    <RiCloseCircleLine size="16" className="me-1" />
                    {t("Unable to save changes")}
                </small>
            )}

            {handleCompanyMutation.isSuccess && (
                <small className="text-success">
                    <RiCheckLine size="16" className="me-1" />
                    {t("Changes saved")}
                </small>
            )}
            </Col>

            <Col sm="12" md="3" className="mb-4">
              <Label className="form-label">{t("Position")}:</Label>
              <Input
                name="position"
                type="text"
                className="form-control"
                onChange={debouncedHandlePositionChange}
                id="position"
                defaultValue={selectedPosition}
              />
                {handlePositionMutation.isLoading && (
                    <>
                        <Spinner size='sm' color="info" />
                        <small className="text-info ms-1">
                            {t("Saving")}...
                        </small>
                    </>
                )}

                {handlePositionMutation.isError && (
                    <small className="text-danger">
                        <RiCloseCircleLine size="16" className="me-1" />
                        {t("Unable to save changes")}
                    </small>
                )}

                {handlePositionMutation.isSuccess && (
                    <small className="text-success">
                        <RiCheckLine size="16" className="me-1" />
                        {t("Changes saved")}
                    </small>
                )}
            </Col>

            <Col sm="12" md="3" className="mb-4">
              <Label className="form-label">{t("Email")}:</Label>
              <AvForm>
                <AvField
                  name="email"
                  placeholder=""
                  type="email"
                  className="form-control"
                  validate={{ required: { value: false }, email: true }}
                  errorMessage={t("This field is invalid")}
                  id="email"
                    onChange={(_e, value) => {
                        (!value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) && debouncedHandleEmailChange(value);
                    }}
                  value={selectedEmail}
                />

                {handleCreateEmailMutation.isLoading && (
                    <>
                        <Spinner size='sm' color="info" />
                        <small className="text-info ms-1">
                            {t("Saving")}...
                        </small>
                    </>
                )}

                {handleCreateEmailMutation.isError && (
                    <small className="text-danger">
                        <RiCloseCircleLine size="16" className="me-1" />
                        {t("Unable to save changes")}
                    </small>
                )}

                {handleCreateEmailMutation.isSuccess && (
                    <small className="text-success">
                        <RiCheckLine size="16" className="me-1" />
                        {t("Changes saved")}
                    </small>
                )}
              </AvForm>
            </Col>

            <Col sm="12" md="3">
              <Label className="form-label">{t("Phone Number")}:</Label>
              <AvForm>
                <AvField
                  name={`phone`}
                  errorMessage={t("This field cannot be blank")}
                  type="number"
                  onChange={debouncedHandlePhoneNumberChange}
                  value={selectedPhoneNumber}
                />

                {handleCreatePhoneNumberMutation.isLoading && (
                    <>
                        <Spinner size='sm' color="info" />
                        <small className="text-info ms-1">
                            {t("Saving")}...
                        </small>
                    </>
                )}

                {handleCreatePhoneNumberMutation.isError && (
                    <small className="text-danger">
                        <RiCloseCircleLine size="16" className="me-1" />
                        {t("Unable to save changes")}
                    </small>
                )}

                {handleCreatePhoneNumberMutation.isSuccess && (
                    <small className="text-success">
                        <RiCheckLine size="16" className="me-1" />
                        {t("Changes saved")}
                    </small>
                )}
              </AvForm>
            </Col>
          </Row>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default withNamespaces()(ResponsiblePerson);
